import * as React from 'react';

import { ActivityType } from '@showbie-socrative/socrative-utils';

import { parseResultData } from '~teacher/components/ActivityResult/util/parseResultData';
import { ResultsGrid } from '~teacher/components/ResultsGrid';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import {
  Activity,
  ActivitySettingsCamelCase,
  ApiQuestionResponse,
  Room,
  Student,
} from '~teacher/models/activities';
import { Quiz, QuizAnswer, QuizQuestion } from '~teacher/models/quizzes';

import { StudentScore, StudentScoreProvider } from '../StudentScore';
import { useResultState } from './ActivityResultProvider';
import { normalizeQuestions } from './util/normalizeQuestions';
import { normalizeResponses } from './util/normalizeResponses';
import { normalizeStudent } from './util/normalizeStudent';

const userRequestedGrid = new Set<number>();

export function suppressQQSARedirect(activityId: number) {
  userRequestedGrid.add(activityId);
}

interface Props {
  activity: Activity;
  students: Record<string, Student>;
  responses: Record<string, ApiQuestionResponse>;
  quiz: Quiz;
  quizQuestion: Record<string, QuizQuestion>;
  quizAnswer: Record<string, QuizAnswer>;
  room: Room;
  showInsights?: boolean;
}

const ActivityResultFinished = ({ showInsights = false, ...props }: Props) => {
  const { questionScores } = useResultState();
  const socrativeRouter = useSocrativeRouter();
  const activity = props.activity;
  const settings = activity.settings as unknown as ActivitySettingsCamelCase;
  const hasTimers = (settings?.quizTimerSeconds ?? -1) > 0;

  const shouldShowAllResponses =
    activity.activityType === ActivityType.QuickQuestionShortAnswer &&
    settings?.allowRepeatResponses === true;

  React.useEffect(() => {
    if (shouldShowAllResponses && !userRequestedGrid.has(activity.id)) {
      socrativeRouter.routeToResultsQuestion(activity.id, 1, true);
    }
  }, [shouldShowAllResponses, activity.id, socrativeRouter]);

  const answers = props.quizAnswer;
  const responses = normalizeResponses({
    quizQuestion: props.quizQuestion,
    students: props.students,
    responses: props.responses,
  });
  const questions = normalizeQuestions(props.quizQuestion);
  const students = normalizeStudent(props.students);

  const parsedActivityData = parseResultData({
    activity,
    answers,
    responses,
    questions,
    students,
    questionScores,
  });

  return (
    <StudentScoreProvider>
      <ResultsGrid
        showInsights={showInsights}
        resultsTableQuestions={parsedActivityData.resultsTableQuestions}
        participants={parsedActivityData.participants}
        maxScore={parsedActivityData.maxScore}
        activityInstanceId={parsedActivityData.activityInstanceId}
        activityStage={parsedActivityData.activityStage}
        isRosteredRoom={props.room.isRostered}
        passRate={parsedActivityData.passRate ?? null}
        totalStudents={activity.totalStudents}
        presentStudents={activity.presentStudents}
        completionRate={activity.completionRate}
        averageScoreRate={activity.averageScoreRate}
        passingStudentsRate={activity.passingStudentsRate ?? undefined}
        hasTimers={hasTimers}
        quizTimerDurationSeconds={settings?.quizTimerSeconds ?? 0}
      />
      <StudentScore
        activity={activity}
        questions={questions}
        responses={responses}
        students={students}
      />
    </StudentScoreProvider>
  );
};

export { ActivityResultFinished };
